import { gql, useQuery } from '@apollo/client';
import { useLogger } from '@amzn/xb-hooks';

export const LEARNER_ATTENDANCE = gql`
    query LearnerAttendance($userId: ID!, $learningActivityId: ID!) {
        userById(id: $userId) {
            attendanceForClass(learningActivityId: $learningActivityId)
        }
    }
`;

/**
 * Attendance outcome for a learner in one class, as marked by the instructor in AMS.
 *
 * Percentage thresholds are applied by the attendance service, not here: at or above 60% of the
 * class duration is ATTENDED, above zero but under it is PARTIALLY_ATTENDED, and zero is NO_SHOW.
 * NOT_MARKED is the initial state on registration; REMOVED follows a withdrawal.
 */
export enum AttendanceStatus {
    NOT_MARKED = 'NOT_MARKED',
    REMOVED = 'REMOVED',
    NO_SHOW = 'NO_SHOW',
    ATTENDED = 'ATTENDED',
    PARTIALLY_ATTENDED = 'PARTIALLY_ATTENDED',
}

export interface LearnerAttendanceData {
    // Null when the learner has no attendance record for the class at all.
    userById: {
        attendanceForClass: AttendanceStatus | null;
    } | null;
}

/**
 * Reads the learner's attendance for a class.
 *
 * Only called once the Premium Content tab is opened, because Cloudscape Tabs mounts the active
 * tab's content and nothing else. Uses network-only so reopening the tab re-reads AMS rather than
 * showing a cached status an instructor has since changed.
 */
export const useLearnerAttendance = (userId: string | undefined, learningActivityId: string | undefined) => {
    const { log, count } = useLogger();

    return useQuery<LearnerAttendanceData>(LEARNER_ATTENDANCE, {
        skip: !userId || !learningActivityId,
        fetchPolicy: 'network-only',
        variables: {
            userId,
            learningActivityId,
        },
        onError: (error) => {
            log.error('Error fetching learner attendance', error);
            count('LearnerAttendance.Error');
        },
    });
};
